import { useEffect, useState, type CSSProperties } from "react";

/**
 * Loading placeholders shaped like the thing that is coming.
 *
 * A spinner says "something is happening". A skeleton says "a table with five rows
 * is arriving here", which is the difference between waiting and knowing what you
 * are waiting for. That only holds if the placeholder actually matches the layout
 * it replaces — a generic grey box in the wrong shape is a spinner with extra
 * steps, and the page still jumps when the real content lands.
 *
 * Two things here matter more than the boxes:
 *
 *  1. It does not flash. Below `DELAY_MS` nothing is shown at all, because a
 *     placeholder that appears and vanishes inside a tenth of a second reads as a
 *     glitch, and this dashboard answers most requests in well under that.
 *  2. It still speaks. Replacing the words "Loading the work" with silent grey
 *     rectangles removes the only signal a screen reader had. Every skeleton is a
 *     live region that announces what is loading.
 */

const DELAY_MS = 150;

/** True once the wait has lasted long enough to be worth drawing. */
export function useSlowEnoughToShow(active: boolean, delay = DELAY_MS) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!active) { setShow(false); return; }
    const timer = window.setTimeout(() => setShow(true), delay);
    return () => window.clearTimeout(timer);
  }, [active, delay]);
  return show;
}

export function Skeleton({ width = "100%", height = 12, radius = 4, style }: {
  width?: number | string;
  height?: number | string;
  radius?: number;
  style?: CSSProperties;
}) {
  return <span className="skeleton" style={{ width, height, borderRadius: radius, ...style }} aria-hidden="true" />;
}

/** Paragraph-ish. The last line is short because real text almost never fills it,
 *  and a block of equal-length bars reads as a table, not prose. */
export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <span className="skeleton-text" aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} width={index === lines - 1 ? "62%" : "100%"} height={11} />
      ))}
    </span>
  );
}

/**
 * The wrapper every skeleton goes through. `role="status"` plus the visually
 * hidden label is what keeps the page audible while it is only shapes.
 */
export function SkeletonRegion({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="skeleton-region" role="status" aria-busy="true">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** Four metric cards, an icon and two lines each — the overview's own shape. */
export function MetricCardsSkeleton({ label = "Loading credit summary" }: { label?: string }) {
  return (
    <SkeletonRegion label={label}>
      <section className="metric-grid" aria-hidden="true">
        {Array.from({ length: 4 }, (_, index) => (
          <article className="metric-card" key={index}>
            <Skeleton width={38} height={38} radius={7} />
            <Skeleton width="55%" height={10} style={{ marginTop: 13 }} />
            <Skeleton width="38%" height={23} style={{ marginTop: 9 }} />
            <Skeleton width="46%" height={9} style={{ marginTop: 8 }} />
          </article>
        ))}
      </section>
    </SkeletonRegion>
  );
}

/** Rows of roughly equal height, matching a data table rather than a list. */
export function TableSkeleton({ rows = 5, label = "Loading" }: { rows?: number; label?: string }) {
  return (
    <SkeletonRegion label={label}>
      <div className="skeleton-rows" aria-hidden="true">
        {Array.from({ length: rows }, (_, index) => (
          <div className="skeleton-row" key={index}>
            <Skeleton width={17} height={17} radius={4} />
            <span>
              <Skeleton width={`${58 - (index % 3) * 9}%`} height={12} />
              <Skeleton width={`${40 - (index % 4) * 5}%`} height={9} style={{ marginTop: 6 }} />
            </span>
            <Skeleton width={54} height={20} radius={10} />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/**
 * Gallery cards.
 *
 * Built from the REAL classes — `.gallery-grid`, `.gallery-card`, `.gallery-body` —
 * rather than a lookalike grid. A hand-copied placeholder drifts the moment someone
 * changes the real one: the first version of this used a 4/3 tile on a 210px grid
 * against a 16/9 card on a 268px grid, so the page jumped on every load. Sharing
 * the classes makes that impossible instead of merely unlikely.
 */
export function GalleryCardsSkeleton({ cards = 6, label = "Loading" }: { cards?: number; label?: string }) {
  return (
    <SkeletonRegion label={label}>
      <div className="gallery-grid" aria-hidden="true">
        {Array.from({ length: cards }, (_, index) => (
          <article className="gallery-card" key={index}>
            <div className="skeleton-frame" style={{ aspectRatio: "16 / 9" }}>
              <Skeleton width="100%" height="100%" radius={0} />
            </div>
            <div className="gallery-body">
              <Skeleton width={`${72 - (index % 3) * 12}%`} height={13} />
              <Skeleton width="46%" height={11} style={{ marginTop: 5 }} />
              <span className="skeleton-meta">
                <Skeleton width={62} height={9} />
                <Skeleton width={74} height={9} />
              </span>
            </div>
          </article>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** One large frame and a few lines — the shape of a single hand-off screen. */
export function HeroSkeleton({ label = "Loading" }: { label?: string }) {
  return (
    <SkeletonRegion label={label}>
      <div className="skeleton-hero" aria-hidden="true">
        <div className="skeleton-frame" style={{ aspectRatio: "16 / 9" }}>
          <Skeleton width="100%" height="100%" radius={0} />
        </div>
        <div className="skeleton-hero-body">
          <Skeleton width="46%" height={19} />
          <Skeleton width="28%" height={11} style={{ marginTop: 9 }} />
          <span style={{ display: "block", marginTop: 18 }}><SkeletonText lines={3} /></span>
        </div>
      </div>
    </SkeletonRegion>
  );
}
